import {
  alpha,
  Box,
  Chip,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SwipeableDrawer,
  Toolbar,
  Typography,
} from '@mui/material';

import { type MouseEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import type { NavSection } from '../../config/drawer-route-config';
import { getNavSections } from '../../config/drawer-route-config';
import { RoleNames } from '../../config/role-names';
import { hasRequiredPermissions, ROUTE_PERMISSION_MAP } from '../../config/route-permission-map';
import { useConsultantAccess } from '../../hooks/consultant/use-consultant-access';
import { useMultiSiteAccess } from '../../hooks/my-sites/use-multi-site-access';
import { useSubscription } from '../../hooks/subscription/use-subscription';
import { useAuthUser } from '../../hooks/use-auth-user';
import { useIsMobile } from '../../hooks/use-is-mobile';
import { usePermissions } from '../../hooks/use-permissions';

import { colors, NAVIGATION_DRAWER_CLASS } from '../../theme/ai-theme';
import { BetaChip } from '../beta-chip';
import { UserButtonWrapper } from '../user-button-wrapper';

interface SideDrawerProps {
  className?: string;
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
  variant: 'permanent' | 'temporary';
  drawerWidth: number;
}

// Offset verticale della SwipeArea dello SwipeableDrawer (la striscia
// invisibile swipe-to-open): MUI la fissa a top:0 con zIndex 1199, cioè sopra
// l'AppBar (1100). A top:0 il suo bordo sinistro da 20px copriva il lato
// sinistro dell'hamburger: un tap lì non raggiungeva il bottone ma avviava il
// gesto swipe (maybeSwiping), che al touchend senza movimento si richiudeva —
// il drawer "si apre e richiude immediatamente". La striscia va sotto la
// navbar: Toolbar 56px (xs) / 64px (sm+) + 5px di bordo inferiore
// dell'AppBar (il valore copre anche l'inset verticale del notch via env()).
// Esportata per il test: happy-dom non parsa env() nel CSS, quindi il valore
// non è verificabile via DOM (SideDrawer.swipe-area.test.tsx).
export const SWIPE_AREA_TOP = 'calc(env(safe-area-inset-top, 0px) + 64px)';

const listItemButtonStyles = {
  borderRadius: 2,
  mb: 0.5,
  transition: 'background-color 0.2s ease',
  // Stato default: colori chiari su sfondo scuro
  '& .MuiListItemText-primary': {
    color: '#E2E8F0', // Contrasto 9.7:1 su #1E293B - WCAG AAA
    transition: 'color 0.2s ease',
  },
  '& .MuiListItemIcon-root': {
    color: '#E2E8F0', // Stesso colore del testo per coerenza
    transition: 'color 0.2s ease',
  },
  '&:hover': {
    backgroundColor: '#334155',
    '& .MuiListItemText-primary': {
      color: '#F8FAFC',
    },
    '& .MuiListItemIcon-root': {
      color: '#F8FAFC',
    },
  },
  '&.Mui-selected': {
    backgroundColor: '#334155',
    '&:hover': {
      backgroundColor: '#3D4F66',
    },
    '& .MuiListItemIcon-root': {
      color: '#FFFFFF', // Contrasto 11.7:1 - WCAG AAA
    },
    '& .MuiListItemText-primary': {
      color: '#FFFFFF', // Contrasto 11.7:1 - WCAG AAA
      fontWeight: 600,
    },
  },
};

interface NavItemConfig {
  path: string;
  label: string;
  icon: React.ReactNode;
  external?: boolean;
  beta?: boolean;
  hideForRoles?: string[];
  requiresDdtAddon?: boolean;
  requiresSurveyAddon?: boolean;
  requiresPrepackagedLabelAddon?: boolean;
  requiresIotAddon?: boolean;
  requiresMenuAddon?: boolean;
  requiresConsultantAccess?: boolean;
  requiresPartnerAccess?: boolean;
  hideWithConsultantAccess?: boolean;
  requiresMultiSiteAccess?: boolean;
}

interface NavItemProps {
  item: NavItemConfig;
  isSelected: boolean;
  onNavigate: (path: string) => void;
}

function NavItem({ item, isSelected, onNavigate }: NavItemProps) {
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    // I click nel dialog del chip Beta risalgono a questa voce attraverso il portale React.
    if (!event.currentTarget.contains(event.target as Node)) {
      return;
    }
    if (item.external) {
      window.open(item.path, '_blank', 'noopener,noreferrer');
    } else {
      onNavigate(item.path);
    }
  };

  return (
    <ListItemButton
      key={item.path}
      selected={isSelected}
      onClick={handleClick}
      sx={listItemButtonStyles}
    >
      <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
      <ListItemText primary={item.label} />
      {item.beta && <BetaChip />}
    </ListItemButton>
  );
}

interface FilterItemParams {
  item: NavItemConfig & {
    isLoggedIn?: boolean;
    adminOnly?: boolean;
    hideForRoles?: string[];
    showForRoles?: string[];
  };
  isLoggedIn: unknown;
  roleName: string | null | undefined;
  isPlatformAdmin: boolean;
  permissionsLoading: boolean;
  hasPermission: (permission: string) => boolean;
  ddtAddonEnabled?: boolean;
  surveyAddonEnabled?: boolean;
  prepackagedLabelEnabled?: boolean;
  iotAddonEnabled?: boolean;
  menuAddonEnabled?: boolean;
  hasConsultantAccess?: boolean;
  isPartner?: boolean;
  hasMultiSiteAccess?: boolean;
}

function isHiddenByAddonGate(
  item: FilterItemParams['item'],
  roleName: string | null | undefined,
  ddtAddonEnabled: boolean,
  surveyAddonEnabled: boolean,
  prepackagedLabelEnabled: boolean,
  iotAddonEnabled: boolean,
  menuAddonEnabled: boolean,
  hasConsultantAccess: boolean,
  isPartner: boolean,
  hasMultiSiteAccess: boolean,
): boolean {
  if (item.requiresDdtAddon && !ddtAddonEnabled) {
    return true;
  }
  if (item.requiresSurveyAddon && !surveyAddonEnabled && roleName !== RoleNames.CONSULTANT) {
    return true;
  }
  if (
    item.requiresPrepackagedLabelAddon &&
    !prepackagedLabelEnabled &&
    roleName !== RoleNames.CONSULTANT
  ) {
    return true;
  }
  if (item.requiresIotAddon && !iotAddonEnabled) {
    return true;
  }
  if (item.requiresMenuAddon && !menuAddonEnabled) {
    return true;
  }
  if (item.requiresConsultantAccess && !hasConsultantAccess) {
    return true;
  }
  if (item.requiresMultiSiteAccess && !hasMultiSiteAccess) {
    return true;
  }
  return !!item.requiresPartnerAccess && !isPartner;
}

function isHiddenByRoleRules(
  item: FilterItemParams['item'],
  roleName: string | null | undefined,
  hasConsultantAccess: boolean,
): boolean {
  if (item.hideWithConsultantAccess && hasConsultantAccess) {
    return true;
  }
  if (item.hideForRoles && roleName && item.hideForRoles.includes(roleName)) {
    return true;
  }
  return !!item.showForRoles && !!roleName && !item.showForRoles.includes(roleName);
}

export function shouldIncludeItem({
  item,
  isLoggedIn,
  roleName,
  isPlatformAdmin,
  permissionsLoading,
  hasPermission,
  ddtAddonEnabled = true,
  surveyAddonEnabled = true,
  prepackagedLabelEnabled = true,
  iotAddonEnabled = true,
  menuAddonEnabled = true,
  hasConsultantAccess = false,
  isPartner = false,
  hasMultiSiteAccess = false,
}: FilterItemParams): boolean {
  if (item.isLoggedIn !== !!isLoggedIn) {
    return false;
  }
  if (
    isHiddenByAddonGate(
      item,
      roleName,
      ddtAddonEnabled,
      surveyAddonEnabled,
      prepackagedLabelEnabled,
      iotAddonEnabled,
      menuAddonEnabled,
      hasConsultantAccess,
      isPartner,
      hasMultiSiteAccess,
    )
  ) {
    return false;
  }
  if (item.adminOnly && !isPlatformAdmin) {
    return false;
  }
  if (isHiddenByRoleRules(item, roleName, hasConsultantAccess)) {
    return false;
  }
  // Platform Admin sees only adminOnly items + account/auth items (no HACCP modules)
  // unless the item explicitly whitelists 'Admin' via showForRoles.
  if (isPlatformAdmin && !item.adminOnly && !item.showForRoles?.includes('Admin')) {
    const adminAllowedPaths = [
      '/team',
      '/profile',
      '/settings',
      '/logout',
      '/sign-in',
      '/sign-up',
      '/guide',
    ];
    if (!adminAllowedPaths.includes(item.path)) {
      return false;
    }
  }
  // While permissions are loading, show all items to avoid empty flash
  if (permissionsLoading) {
    return true;
  }
  const requiredPermission = ROUTE_PERMISSION_MAP[item.path];
  if (!requiredPermission) {
    return true;
  }
  return hasRequiredPermissions(requiredPermission, hasPermission);
}

export function isNavItemSelected(pathname: string, itemPath: string): boolean {
  return pathname === itemPath || (itemPath !== '/' && pathname.startsWith(`${itemPath}/`));
}

interface FilterNavSectionsParams {
  isLoggedIn: unknown;
  roleName: string | null | undefined;
  isPlatformAdmin: boolean;
  permissionsLoading: boolean;
  hasPermission: (permission: string) => boolean;
  ddtAddonEnabled?: boolean;
  surveyAddonEnabled?: boolean;
  prepackagedLabelEnabled?: boolean;
  iotAddonEnabled?: boolean;
  menuAddonEnabled?: boolean;
  hasConsultantAccess?: boolean;
  isPartner?: boolean;
  hasMultiSiteAccess?: boolean;
  isMobile?: boolean;
}

/**
 * Filtra le sezioni della navigation: scarta le sezioni senza item visibili e
 * applica il gate strutturale alle sezioni `consultantTools`.
 *
 * Il gate è a livello di SEZIONE, indipendente dai flag dei singoli item:
 * anche se un futuro item venisse aggiunto alla sezione senza
 * `requiresConsultantAccess`, la sezione resta nascosta a chi non ha accesso
 * consulente (la regola non è più emergente dal filtraggio per-item).
 */
export function filterNavSections(
  sections: NavSection[],
  {
    isMobile = false,
    hasConsultantAccess = false,
    isPartner = false,
    hasMultiSiteAccess = false,
    ...itemFilter
  }: FilterNavSectionsParams,
): NavSection[] {
  return sections.flatMap((section) => {
    if (section.consultantTools && !hasConsultantAccess) {
      return [];
    }
    const filteredItems = section.items.filter((item) => {
      if (isMobile && item.hideOnMobile) {
        return false;
      }
      // hasConsultantAccess va re-inserito esplicitamente: la destrutturazione
      // qui sopra lo esclude da `itemFilter`, e senza di lui gli item con
      // `requiresConsultantAccess` / `hideWithConsultantAccess` vedrebbero
      // sempre il default `false` (sezione consulente sempre vuota → mai
      // renderizzata, gemello Survey mai nascosto). Idem `isPartner` per gli
      // item con `requiresPartnerAccess` e `hasMultiSiteAccess` per
      // `requiresMultiSiteAccess`.
      return shouldIncludeItem({
        item,
        ...itemFilter,
        hasConsultantAccess,
        isPartner,
        hasMultiSiteAccess,
      });
    });
    return filteredItems.length > 0 ? [{ ...section, items: filteredItems }] : [];
  });
}

export default function SideDrawer({
  className,
  open,
  onClose,
  onOpen,
  variant,
  drawerWidth,
}: SideDrawerProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isLoggedIn = useAuthUser();
  const { t } = useTranslation();
  const {
    hasPermission,
    isLoading: permissionsLoading,
    isPlatformAdmin,
    roleName,
  } = usePermissions();
  const {
    subscriptionStatus,
    ddtAddonEnabled,
    surveyAddonEnabled,
    prepackagedLabelEnabled,
    iotAddonEnabled,
    menuAddonEnabled,
  } = useSubscription();
  const isCanceled = subscriptionStatus === 'CANCELED' || subscriptionStatus === 'EXPIRED';
  const isReadOnly = subscriptionStatus === 'PAST_DUE';
  const isMobile = useIsMobile();
  // Cross-tenant signal for the /consultant/clients drawer entry: the backend's
  // own guard decision (membership OR referral-link ownership), independent of
  // the tenant-scoped `roleName` above, so it still shows for a consultant
  // switched into one of their own tenants AND for a referral-only owner
  // without any Consulente membership (see drawer-route-config.tsx).
  // `isPartner` è il flag per la voce Partner: Consulente o Owner di sistema
  // di un tenant partner, indipendente dal tenant corrente (stessa risposta
  // di `GET /consultant/access`).
  const { hasConsultantAccess, isPartner } = useConsultantAccess();
  // Cross-tenant signal per la voce "Le mie sedi": stesso pattern di
  // `hasConsultantAccess` sopra, ma indipendente da esso — un utente può
  // essere multi-sede senza essere consulente e viceversa.
  const { hasMultiSiteAccess } = useMultiSiteAccess();

  const navSections = getNavSections(t);

  const visibleSections = filterNavSections(navSections, {
    isLoggedIn,
    roleName,
    isPlatformAdmin,
    permissionsLoading,
    hasPermission,
    ddtAddonEnabled,
    surveyAddonEnabled,
    prepackagedLabelEnabled,
    iotAddonEnabled,
    menuAddonEnabled,
    hasConsultantAccess,
    isPartner,
    hasMultiSiteAccess,
    isMobile,
  });

  const handleNavigation = useCallback(
    function handleNavigation(path: string) {
      navigate(path);

      const isTemporary = variant === 'temporary';
      if (isTemporary) {
        onClose();
      }
    },
    [navigate, variant, onClose],
  );

  const drawerSx = {
    width: drawerWidth,
    flexShrink: 0,
    '& .MuiDrawer-paper': {
      width: drawerWidth,
      boxSizing: 'border-box',
    },
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar
        sx={{
          borderBottom: `1px solid ${alpha('#FFFFFF', 0.1)}`,
          paddingTop: 'env(safe-area-inset-top, 0px)',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: colors.navigation.text,
          }}
        >
          {t('app.name')}
        </Typography>
        {isCanceled && (
          <Chip
            label={t('billing.badge.canceled')}
            size="small"
            color="error"
            sx={{ ml: 1, fontWeight: 600, fontSize: '0.65rem', height: 22 }}
          />
        )}
        {isReadOnly && (
          <Chip
            label={t('billing.badge.readOnly')}
            size="small"
            color="warning"
            sx={{ ml: 1, fontWeight: 600, fontSize: '0.65rem', height: 22 }}
          />
        )}
        <Box sx={{ ml: 'auto' }}>
          <UserButtonWrapper />
        </Box>
      </Toolbar>
      <List
        sx={{
          flex: 1,
          pt: 2,
          px: 2,
          overflowY: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {visibleSections.map((section, sectionIndex) => {
          const filteredItems = section.items;

          // Sezione "Strumenti del consulente": pannello con tinta dedicata
          // (accent ciano del modulo consulente) per distinguerla a colpo
          // d'occhio dal resto della navigazione.
          const isConsultantTools = section.consultantTools === true;

          return (
            <Box
              key={section.title ?? `section-${sectionIndex}`}
              sx={
                isConsultantTools
                  ? {
                      backgroundColor: 'rgba(14, 116, 144, 0.16)',
                      border: '1px solid rgba(14, 116, 144, 0.5)',
                      borderRadius: 2,
                      px: 1,
                      pt: 0.5,
                      pb: 1,
                      mb: 1,
                    }
                  : undefined
              }
            >
              {section.title && (
                <Typography
                  variant="caption"
                  sx={{
                    px: 2,
                    py: 1,
                    display: 'block',
                    color: isConsultantTools ? '#67E8F9' : '#94A3B8',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontSize: '0.7rem',
                  }}
                >
                  {section.title}
                </Typography>
              )}
              {filteredItems.map((item) => {
                const isSelected = isNavItemSelected(location.pathname, item.path);

                return (
                  <NavItem
                    key={item.path}
                    item={item}
                    isSelected={isSelected}
                    onNavigate={handleNavigation}
                  />
                );
              })}
              {!isConsultantTools && sectionIndex < visibleSections.length - 1 && (
                <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />
              )}
            </Box>
          );
        })}
      </List>
    </Box>
  );

  if (variant === 'temporary') {
    return (
      <SwipeableDrawer
        className={className}
        open={open}
        onClose={onClose}
        onOpen={onOpen}
        disableBackdropTransition
        slotProps={{
          paper: { className: NAVIGATION_DRAWER_CLASS },
          swipeArea: {
            // Vedi SWIPE_AREA_TOP: senza questo offset la striscia copre il
            // lato sinistro dell'hamburger e i tap sull'icona richiudono il
            // drawer appena aperto.
            style: { top: SWIPE_AREA_TOP },
          },
        }}
        sx={drawerSx}
      >
        {drawerContent}
      </SwipeableDrawer>
    );
  }

  return (
    <Drawer
      className={className}
      variant="permanent"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { className: NAVIGATION_DRAWER_CLASS } }}
      sx={drawerSx}
    >
      {drawerContent}
    </Drawer>
  );
}
